import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getRole, getToken, login } from "../services/auth";
import "./Login.css";

type LoginType = "clinic" | "admin" | null;

function homeForRole(role?: string | null): string {
  if (!role) return "/reception";
  const r = role.toLowerCase();
  if (r === "admin") return "/admin";
  if (r === "doctor") return "/doctor";
  if (r === "manager") return "/manager";
  if (r === "pharmacy") return "/pharmacy";
  if (r === "reception" || r === "clinic") return "/reception";
  return "/reception";
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [loginType, setLoginType] = useState<LoginType>(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Username is derived from login type
  const username = loginType === "admin" ? "admin" : "clinic";

  useEffect(() => {
    if (getToken()) {
      navigate(homeForRole(getRole()), { replace: true });
      return;
    }
    const reason = searchParams.get("reason");
    if (reason === "expired" || sessionStorage.getItem("auth:expired") === "1") {
      setNotice("Session expired. Please login again.");
      sessionStorage.removeItem("auth:expired");
    }
    // Pre-select based on role param
    const roleParam = searchParams.get("role");
    if (roleParam === "admin") setLoginType("admin");
    else if (roleParam) setLoginType("clinic");
  }, [navigate, searchParams]);

  const canSubmit = password.length > 0 && !isSubmitting && loginType !== null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await login(username, password, rememberMe, null);
      const effectiveRole =
        (result.role || "").toLowerCase() === "admin" ? "admin" : "clinic";
      navigate(homeForRole(effectiveRole), { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Login failed.";
      if (/invalid credentials|unauthorized/i.test(message)) {
        setError("Incorrect password. Please try again.");
      } else {
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const EyeIcon = ({ open }: { open: boolean }) => (
    <svg className="eye-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {open ? (
        <>
          <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
        </>
      ) : (
        <>
          <path d="M3 4l18 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M4.5 7.5C2.6 9.4 2 12 2 12s3.5 6 10 6c2 0 3.7-.5 5.2-1.3M8.2 6.6C9.4 6.2 10.6 6 12 6c6.5 0 10 6 10 6s-.9 1.6-2.7 3.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </>
      )}
    </svg>
  );

  return (
    <div className="login-page">
      <div className="login-card">
        {/* Branding */}
        <div className="login-brand">
          <div className="login-logo">TD</div>
          <div>
            <h1>Thaila Dental</h1>
            <p>Select your login type to continue</p>
          </div>
        </div>

        {/* Two-card role selector */}
        <div className="login-type-selector">
          <button
            type="button"
            className={`login-type-card clinic-card ${loginType === "clinic" ? "active" : ""}`}
            onClick={() => { setLoginType("clinic"); setPassword(""); setError(null); }}
          >
            <span className="login-type-icon">🏥</span>
            <span className="login-type-name">Clinic Staff</span>
            <span className="login-type-desc">Reception · Doctor · Manager · Pharmacy</span>
          </button>

          <button
            type="button"
            className={`login-type-card admin-card ${loginType === "admin" ? "active" : ""}`}
            onClick={() => { setLoginType("admin"); setPassword(""); setError(null); }}
          >
            <span className="login-type-icon">🛡️</span>
            <span className="login-type-name">Administrator</span>
            <span className="login-type-desc">Full access · All modules</span>
          </button>
        </div>

        {/* Password form */}
        {loginType && (
          <form className="login-form" onSubmit={handleSubmit}>
            <label>
              Password
              <div className="password-input">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  maxLength={72}
                  autoFocus
                  onChange={(e) => setPassword(e.target.value.slice(0, 72))}
                  placeholder={`Enter ${loginType === "admin" ? "admin" : "clinic"} password`}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>
            </label>

            <label className="login-remember">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              Remember me on this device
            </label>

            {notice && <div className="login-notice">{notice}</div>}
            {error && <div className="login-error">{error}</div>}

            <button
              type="submit"
              disabled={!canSubmit}
              className={loginType === "admin" ? "btn-admin" : "btn-clinic"}
            >
              {isSubmitting ? "Signing in..." : `Sign in as ${loginType === "admin" ? "Admin" : "Clinic Staff"}`}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
